import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Check,
  Loader2,
  BarChart3,
  Search,
  ShoppingBag,
  Megaphone,
  Youtube,
  Globe,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Mail,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getAnalyticsProperties,
  getSearchConsoleSites,
  getMerchantAccounts,
  getYouTubeChannels,
  getAdsAccounts,
  getGmailLabels,
  getGoogleCalendars,
  saveSelectedProperties,
  getSelectedProperties,
  AnalyticsProperty,
  SearchConsoleSite,
  MerchantAccount,
  YouTubeChannel,
  AdsAccount,
  GmailLabel,
  GoogleCalendar,
  SelectedProperties,
} from '../services/googleOAuthService';

interface GoogleServicesSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  userId: string;
  connectionId: string;
  googleEmail: string;
}

type ServiceId = 'analytics' | 'searchConsole' | 'ads' | 'merchant' | 'youtube' | 'gmail' | 'calendar';

interface ServiceConfig {
  id: ServiceId;
  name: string;
  nameEn: string;
  icon: typeof BarChart3;
  color: string;
}

const services: ServiceConfig[] = [
  { id: 'analytics', name: 'Analytics', nameEn: 'Analytics', icon: BarChart3, color: '#E37400' },
  { id: 'searchConsole', name: 'Search Console', nameEn: 'Search Console', icon: Search, color: '#458CF5' },
  { id: 'ads', name: 'Google Ads', nameEn: 'Google Ads', icon: Megaphone, color: '#34A853' },
  { id: 'merchant', name: 'Merchant', nameEn: 'Merchant', icon: ShoppingBag, color: '#4285F4' },
  { id: 'youtube', name: 'YouTube', nameEn: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'gmail', name: 'Gmail', nameEn: 'Gmail', icon: Mail, color: '#EA4335' },
  { id: 'calendar', name: 'Calendar', nameEn: 'Calendar', icon: Calendar, color: '#4285F4' },
];

export function GoogleServicesSetupModal({
  isOpen,
  onClose,
  onComplete,
  userId,
  connectionId,
  googleEmail,
}: GoogleServicesSetupModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const [activeService, setActiveService] = useState<ServiceId>('analytics');
  const [loading, setLoading] = useState<Record<ServiceId, boolean>>({
    analytics: false,
    searchConsole: false,
    ads: false,
    merchant: false,
    youtube: false,
    gmail: false,
    calendar: false,
  });
  const [errors, setErrors] = useState<Record<ServiceId, string | null>>({
    analytics: null,
    searchConsole: null,
    ads: null,
    merchant: null,
    youtube: null,
    gmail: null,
    calendar: null,
  });

  // Data for each service
  const [analyticsProperties, setAnalyticsProperties] = useState<AnalyticsProperty[]>([]);
  const [searchConsoleSites, setSearchConsoleSites] = useState<SearchConsoleSite[]>([]);
  const [adsAccounts, setAdsAccounts] = useState<AdsAccount[]>([]);
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>([]);
  const [youtubeChannels, setYoutubeChannels] = useState<YouTubeChannel[]>([]);
  const [gmailLabels, setGmailLabels] = useState<GmailLabel[]>([]);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);

  // Selected items for each service
  const [selectedAnalytics, setSelectedAnalytics] = useState<Set<string>>(new Set());
  const [selectedSearchConsole, setSelectedSearchConsole] = useState<Set<string>>(new Set());
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());
  const [selectedMerchant, setSelectedMerchant] = useState<Set<string>>(new Set());
  const [selectedYoutube, setSelectedYoutube] = useState<Set<string>>(new Set());
  const [selectedGmail, setSelectedGmail] = useState<Set<string>>(new Set());
  const [selectedCalendar, setSelectedCalendar] = useState<Set<string>>(new Set());

  const [saving, setSaving] = useState(false);
  const [loadedServices, setLoadedServices] = useState<Set<ServiceId>>(new Set());
  const [existingSelectionsLoaded, setExistingSelectionsLoaded] = useState(false);

  // Load existing selections when modal opens
  useEffect(() => {
    if (isOpen && !existingSelectionsLoaded) {
      loadExistingSelections();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setExistingSelectionsLoaded(false);
      setLoadedServices(new Set());
    }
  }, [isOpen]);

  const loadExistingSelections = async () => {
    try {
      const existing = await getSelectedProperties(userId, connectionId);
      if (existing) {
        // Set selected analytics
        if (existing.analytics) {
          setSelectedAnalytics(new Set(existing.analytics.map(a => a.propertyId)));
        }
        // Set selected search console sites
        if (existing.searchConsole) {
          setSelectedSearchConsole(new Set(existing.searchConsole.map(s => s.siteUrl)));
        }
        // Set selected ads accounts
        if (existing.ads) {
          setSelectedAds(new Set(existing.ads.map(a => a.customerId)));
        }
        // Set selected merchant accounts
        if (existing.merchant) {
          setSelectedMerchant(new Set(existing.merchant.map(m => m.merchantId)));
        }
        // Set selected YouTube channels
        if (existing.youtube) {
          setSelectedYoutube(new Set(existing.youtube.map(y => y.channelId)));
        }
        // Set selected Gmail labels
        if (existing.gmail) {
          setSelectedGmail(new Set(existing.gmail.map(g => g.labelId)));
        }
        // Set selected Calendars
        if (existing.calendar) {
          setSelectedCalendar(new Set(existing.calendar.map(c => c.calendarId)));
        }
      }
      setExistingSelectionsLoaded(true);
    } catch (error) {
      console.error('Failed to load existing selections:', error);
      setExistingSelectionsLoaded(true);
    }
  };

  // Load data when service tab is selected
  useEffect(() => {
    if (isOpen && !loadedServices.has(activeService)) {
      loadServiceData(activeService);
    }
  }, [isOpen, activeService]);

  const loadServiceData = async (serviceId: ServiceId) => {
    if (loadedServices.has(serviceId)) return;

    setLoading((prev) => ({ ...prev, [serviceId]: true }));
    setErrors((prev) => ({ ...prev, [serviceId]: null }));

    try {
      switch (serviceId) {
        case 'analytics': {
          const data = await getAnalyticsProperties(userId, connectionId);
          setAnalyticsProperties(data);
          break;
        }
        case 'searchConsole': {
          const data = await getSearchConsoleSites(userId, connectionId);
          setSearchConsoleSites(data);
          break;
        }
        case 'ads': {
          const data = await getAdsAccounts(userId, connectionId);
          setAdsAccounts(data);
          break;
        }
        case 'merchant': {
          const data = await getMerchantAccounts(userId, connectionId);
          setMerchantAccounts(data);
          break;
        }
        case 'youtube': {
          const data = await getYouTubeChannels(userId, connectionId);
          setYoutubeChannels(data);
          break;
        }
        case 'gmail': {
          const data = await getGmailLabels(userId, connectionId);
          setGmailLabels(data);
          break;
        }
        case 'calendar': {
          const data = await getGoogleCalendars(userId, connectionId);
          setGoogleCalendars(data);
          break;
        }
      }
      setLoadedServices((prev) => new Set(prev).add(serviceId));
    } catch (err) {
      console.error(`Error loading ${serviceId}:`, err);
      setErrors((prev) => ({
        ...prev,
        [serviceId]: isEnglish ? 'Failed to load data' : 'Veriler yüklenemedi',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [serviceId]: false }));
    }
  };

  const toggleSelection = (serviceId: ServiceId, itemId: string) => {
    const setters: Record<ServiceId, React.Dispatch<React.SetStateAction<Set<string>>>> = {
      analytics: setSelectedAnalytics,
      searchConsole: setSelectedSearchConsole,
      ads: setSelectedAds,
      merchant: setSelectedMerchant,
      youtube: setSelectedYoutube,
      gmail: setSelectedGmail,
      calendar: setSelectedCalendar,
    };

    setters[serviceId]((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getSelectedCount = (serviceId: ServiceId): number => {
    const counts: Record<ServiceId, Set<string>> = {
      analytics: selectedAnalytics,
      searchConsole: selectedSearchConsole,
      ads: selectedAds,
      merchant: selectedMerchant,
      youtube: selectedYoutube,
      gmail: selectedGmail,
      calendar: selectedCalendar,
    };
    return counts[serviceId].size;
  };

  const getTotalSelectedCount = (): number => {
    return (
      selectedAnalytics.size +
      selectedSearchConsole.size +
      selectedAds.size +
      selectedMerchant.size +
      selectedYoutube.size +
      selectedGmail.size +
      selectedCalendar.size
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const selectedProperties: SelectedProperties = {
        analytics: analyticsProperties
          .filter((p) => selectedAnalytics.has(p.propertyId))
          .map((p) => ({
            propertyId: p.propertyId,
            propertyName: p.propertyName,
            accountId: p.accountId,
            accountName: p.accountName,
          })),
        searchConsole: searchConsoleSites
          .filter((s) => selectedSearchConsole.has(s.siteUrl))
          .map((s) => ({ siteUrl: s.siteUrl })),
        ads: adsAccounts
          .filter((a) => selectedAds.has(a.customerId))
          .map((a) => ({ customerId: a.customerId, descriptiveName: a.descriptiveName })),
        merchant: merchantAccounts
          .filter((m) => selectedMerchant.has(m.merchantId))
          .map((m) => ({ merchantId: m.merchantId })),
        youtube: youtubeChannels
          .filter((c) => selectedYoutube.has(c.channelId))
          .map((c) => ({ channelId: c.channelId, title: c.title })),
        gmail: gmailLabels
          .filter((l) => selectedGmail.has(l.labelId))
          .map((l) => ({ labelId: l.labelId, name: l.name })),
        calendar: googleCalendars
          .filter((c) => selectedCalendar.has(c.calendarId))
          .map((c) => ({ calendarId: c.calendarId, summary: c.summary })),
      };

      await saveSelectedProperties(userId, connectionId, selectedProperties);
      onComplete();
      onClose();
    } catch (err) {
      console.error('Error saving properties:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderServiceContent = () => {
    const currentLoading = loading[activeService];
    const currentError = errors[activeService];

    if (currentLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent mb-4" />
          <p className="text-text-secondary">
            {isEnglish ? 'Loading...' : 'Yükleniyor...'}
          </p>
        </div>
      );
    }

    if (currentError) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-red-400 mb-4">{currentError}</p>
          <button
            onClick={() => {
              setLoadedServices((prev) => {
                const newSet = new Set(prev);
                newSet.delete(activeService);
                return newSet;
              });
              loadServiceData(activeService);
            }}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90"
          >
            {isEnglish ? 'Try Again' : 'Tekrar Dene'}
          </button>
        </div>
      );
    }

    switch (activeService) {
      case 'analytics':
        return renderAnalytics();
      case 'searchConsole':
        return renderSearchConsole();
      case 'ads':
        return renderAds();
      case 'merchant':
        return renderMerchant();
      case 'youtube':
        return renderYoutube();
      case 'gmail':
        return renderGmail();
      case 'calendar':
        return renderCalendar();
    }
  };

  const renderAnalytics = () => {
    if (analyticsProperties.length === 0) {
      return renderEmpty(isEnglish ? 'No Analytics properties found' : 'Analytics property bulunamadı');
    }

    // Group by account
    const groupedByAccount = analyticsProperties.reduce((acc, prop) => {
      if (!acc[prop.accountId]) {
        acc[prop.accountId] = { name: prop.accountName, properties: [] };
      }
      acc[prop.accountId].properties.push(prop);
      return acc;
    }, {} as Record<string, { name: string; properties: AnalyticsProperty[] }>);

    return (
      <div className="space-y-4">
        {Object.entries(groupedByAccount).map(([accountId, account]) => (
          <div key={accountId} className="space-y-2">
            <h4 className="text-sm font-medium text-text-secondary px-2">{account.name}</h4>
            {account.properties.map((prop) => (
              <SelectableItem
                key={prop.propertyId}
                selected={selectedAnalytics.has(prop.propertyId)}
                onClick={() => toggleSelection('analytics', prop.propertyId)}
                title={prop.propertyName}
                subtitle={`Property ID: ${prop.propertyId}`}
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderSearchConsole = () => {
    if (searchConsoleSites.length === 0) {
      return renderEmpty(isEnglish ? 'No Search Console sites found' : 'Search Console sitesi bulunamadı');
    }

    return (
      <div className="space-y-2">
        {searchConsoleSites.map((site) => (
          <SelectableItem
            key={site.siteUrl}
            selected={selectedSearchConsole.has(site.siteUrl)}
            onClick={() => toggleSelection('searchConsole', site.siteUrl)}
            title={site.siteUrl}
            subtitle={site.permissionLevel?.replace('site', '')}
            icon={<Globe size={20} className="text-text-tertiary" />}
          />
        ))}
      </div>
    );
  };

  const renderAds = () => {
    if (adsAccounts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Megaphone className="w-12 h-12 text-text-tertiary mb-4" />
          <p className="text-text-secondary mb-2">
            {isEnglish ? 'Google Ads integration coming soon' : 'Google Ads entegrasyonu yakında'}
          </p>
          <p className="text-xs text-text-tertiary max-w-sm">
            {isEnglish
              ? 'Google Ads API requires a Developer Token. This feature will be available in a future update.'
              : 'Google Ads API, Developer Token gerektiriyor. Bu özellik gelecek güncellemede eklenecek.'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {adsAccounts.map((account) => (
          <SelectableItem
            key={account.customerId}
            selected={selectedAds.has(account.customerId)}
            onClick={() => toggleSelection('ads', account.customerId)}
            title={account.descriptiveName || `Account ${account.customerId}`}
            subtitle={`ID: ${account.customerId}`}
            icon={<Megaphone size={20} className="text-text-tertiary" />}
          />
        ))}
      </div>
    );
  };

  const renderMerchant = () => {
    if (merchantAccounts.length === 0) {
      return renderEmpty(isEnglish ? 'No Merchant Center accounts found' : 'Merchant Center hesabı bulunamadı');
    }

    return (
      <div className="space-y-2">
        {merchantAccounts.map((merchant) => (
          <SelectableItem
            key={merchant.merchantId}
            selected={selectedMerchant.has(merchant.merchantId)}
            onClick={() => toggleSelection('merchant', merchant.merchantId)}
            title={`Merchant ID: ${merchant.merchantId}`}
            subtitle={merchant.aggregatorId ? `Aggregator: ${merchant.aggregatorId}` : undefined}
            icon={<ShoppingBag size={20} className="text-text-tertiary" />}
          />
        ))}
      </div>
    );
  };

  const renderYoutube = () => {
    if (youtubeChannels.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Youtube className="w-12 h-12 text-text-tertiary mb-4" />
          <p className="text-text-secondary mb-2">
            {isEnglish ? 'No YouTube channels found' : 'YouTube kanalı bulunamadı'}
          </p>
          <p className="text-xs text-text-tertiary max-w-sm">
            {isEnglish
              ? 'Make sure YouTube Data API v3 is enabled in Google Cloud Console and you have a YouTube channel associated with this account.'
              : 'Google Cloud Console\'da YouTube Data API v3\'ün etkin olduğundan ve bu hesaba bağlı bir YouTube kanalınız olduğundan emin olun.'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {youtubeChannels.map((channel) => (
          <SelectableItem
            key={channel.channelId}
            selected={selectedYoutube.has(channel.channelId)}
            onClick={() => toggleSelection('youtube', channel.channelId)}
            title={channel.title}
            subtitle={`${channel.subscriberCount || '0'} subscribers`}
            icon={
              channel.thumbnailUrl ? (
                <img src={channel.thumbnailUrl} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <Youtube size={20} className="text-text-tertiary" />
              )
            }
          />
        ))}
      </div>
    );
  };

  const renderGmail = () => {
    if (gmailLabels.length === 0) {
      return renderEmpty(isEnglish ? 'No Gmail labels found' : 'Gmail etiketi bulunamadı');
    }

    // Filter out system labels that start with CATEGORY_ or UNREAD/STARRED etc
    const userLabels = gmailLabels.filter(l => l.type === 'user' || ['INBOX', 'SENT', 'DRAFT', 'SPAM', 'TRASH', 'IMPORTANT', 'STARRED'].includes(l.labelId || ''));

    return (
      <div className="space-y-2">
        {userLabels.map((label) => (
          <SelectableItem
            key={label.labelId}
            selected={selectedGmail.has(label.labelId)}
            onClick={() => toggleSelection('gmail', label.labelId)}
            title={label.name}
            subtitle={label.messagesTotal ? `${label.messagesTotal} ${isEnglish ? 'messages' : 'mesaj'}${label.messagesUnread ? ` (${label.messagesUnread} ${isEnglish ? 'unread' : 'okunmamış'})` : ''}` : undefined}
            icon={<Mail size={20} className="text-red-400" />}
          />
        ))}
      </div>
    );
  };

  const renderCalendar = () => {
    if (googleCalendars.length === 0) {
      return renderEmpty(isEnglish ? 'No Google Calendars found' : 'Google Takvim bulunamadı');
    }

    return (
      <div className="space-y-2">
        {googleCalendars.map((cal) => (
          <SelectableItem
            key={cal.calendarId}
            selected={selectedCalendar.has(cal.calendarId)}
            onClick={() => toggleSelection('calendar', cal.calendarId)}
            title={cal.summary}
            subtitle={cal.primary ? (isEnglish ? 'Primary calendar' : 'Ana takvim') : cal.description}
            icon={
              <div
                className="w-5 h-5 rounded"
                style={{ backgroundColor: cal.backgroundColor || '#4285F4' }}
              />
            }
          />
        ))}
      </div>
    );
  };

  const renderEmpty = (message: string) => (
    <div className="flex flex-col items-center justify-center py-12">
      <Globe className="w-12 h-12 text-text-tertiary mb-4" />
      <p className="text-text-secondary">{message}</p>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-bg-surface border border-border-default rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-border-default">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
            <CheckCircle2 size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-text-primary">
              {isEnglish ? 'Set Up Google Services' : 'Google Servislerini Yapılandır'}
            </h3>
            <p className="text-xs text-text-tertiary">{googleEmail}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
          >
            <X size={20} />
          </button>
        </div>

        {/* Service Tabs */}
        <div className="flex overflow-x-auto border-b border-border-default p-2 gap-1">
          {services.map((service) => {
            const Icon = service.icon;
            const count = getSelectedCount(service.id);
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                  activeService === service.id
                    ? 'bg-accent/20 text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                <Icon size={16} style={{ color: activeService === service.id ? undefined : service.color }} />
                <span>{isEnglish ? service.nameEn : service.name}</span>
                {count > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-accent text-white text-xs rounded-full">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{renderServiceContent()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border-default">
          <p className="text-sm text-text-secondary">
            {getTotalSelectedCount()} {isEnglish ? 'total selected' : 'toplam seçildi'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              {isEnglish ? 'Skip' : 'Atla'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
                'bg-accent text-white hover:bg-accent/90',
                saving && 'opacity-50 cursor-not-allowed'
              )}
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {isEnglish ? 'Save & Continue' : 'Kaydet ve Devam Et'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Selectable Item Component
function SelectableItem({
  selected,
  onClick,
  title,
  subtitle,
  icon,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
        selected
          ? 'border-accent bg-accent/10'
          : 'border-border-default bg-bg-elevated hover:border-accent/50'
      )}
    >
      <div
        className={cn(
          'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
          selected ? 'border-accent bg-accent' : 'border-text-tertiary'
        )}
      >
        {selected && <Check size={12} className="text-white" />}
      </div>
      {icon && <div className="flex-shrink-0">{icon}</div>}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary truncate">{title}</p>
        {subtitle && <p className="text-xs text-text-tertiary truncate">{subtitle}</p>}
      </div>
      <ChevronRight size={16} className="text-text-tertiary flex-shrink-0" />
    </button>
  );
}
